import { useCallback, createElement as rc, useState, useEffect, useRef, Fragment } from 'react';
import { useSelection } from './Selection';
import TabGL2025 from './Tab.gl2025';
import { useTheme } from 'styled-components';
import CarouselContainerBase from './CarouselContainer';
import { styled, View, webOnlyStyles, textWidth, fromTheme } from 'lib_ui-primitives';
import useNetwork from '../../../hooks/useNetwork';
import {
    useGradientVisibility,
    useHoverScroll,
    getGradientStyles,
    calculateTotalTabsWidth,
    getSpacingValue
} from './TabCarousel.gl2025.shared';

// Wrap CarouselContainer with border-bottom styling and spacing
const CarouselContainer = styled(CarouselContainerBase)`
    position: relative;
    background-color: ${fromTheme('tabs', 'container', 'backgroundColor')};
    padding-top: ${fromTheme('tabs', 'container', 'paddingTop')};
    border-bottom-width: ${fromTheme('tabs', 'container', 'borderBottomWidth')};
    border-bottom-style: solid;
    border-bottom-color: ${fromTheme('tabs', 'container', 'borderBottomColor')};
    margin-bottom: ${fromTheme('tabs', 'container', 'marginBottom')};
`;

// Horizontal scroll container - hides scrollbar, allows smooth scrolling
let ScrollContainer = styled(View)`
    flex-grow: 1;
    overflow-x: auto;
    overflow-y: hidden;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    position: relative;

    /* Hide scrollbar */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
    &::-webkit-scrollbar {
        display: none; /* Chrome/Safari */
    }

    /* Smooth scrolling */
    scroll-behavior: smooth;
`;

ScrollContainer = webOnlyStyles(ScrollContainer)`
    -webkit-overflow-scrolling: touch;
`;

// Inner container for tabs with spacing
const TabsContainer = styled(View).attrs({ name: 'tabs-container-gl2025', role: 'tablist' })`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: ${({ spacing }) => spacing}px;
    padding-left: ${fromTheme('tabs', 'container', 'paddingLeft')};
    min-width: min-content;
`;

// Empty space to the right of tabs with bottom border
let EmptyTabSpace = styled(View)`
    border-bottom-width: ${fromTheme('tabs', 'container', 'borderBottomWidth')};
    border-bottom-color: ${fromTheme('tabs', 'container', 'borderBottomColor')};
    flex-grow: 1;
    flex-basis: 1px;
    height: ${fromTheme('tabs', 'container', 'emptySpaceHeight')};
`;

EmptyTabSpace = webOnlyStyles(EmptyTabSpace)`
    border-bottom-style: solid;
`;

// Gradient overlay - left side (interactive for hover-to-scroll)
const LeftGradient = styled(View)`
    position: absolute;
    left: 0;
    top: 0;
    width: ${({ gradientStyle }) => gradientStyle.width}px;
    height: ${fromTheme('tabs', 'gradient', 'overlayHeight')};
    background: ${({ gradientStyle }) => gradientStyle.background};
    pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    z-index: 10;
    cursor: pointer;
    opacity: ${({ isHovering }) => (isHovering ? 0.9 : 1)};
    transition: opacity 0.15s ease-in-out;
`;

// Gradient overlay - right side (interactive for hover-to-scroll)
const RightGradient = styled(View)`
    position: absolute;
    right: 0;
    top: 0;
    width: ${({ gradientStyle }) => gradientStyle.width}px;
    height: ${fromTheme('tabs', 'gradient', 'overlayHeight')};
    background: ${({ gradientStyle }) => gradientStyle.background};
    pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    z-index: 10;
    cursor: pointer;
    opacity: ${({ isHovering }) => (isHovering ? 0.9 : 1)};
    transition: opacity 0.15s ease-in-out;
`;

/**
 * Render the actual clickable tab portion that changes the displayed content
 * GL2025 version with horizontal scrolling and gradient overlays
 * @param {object} props
 * @returns TabCarousel component
 */
export default function TabCarouselGL2025(props) {
    const [selectedIndex, setSelectedIndex] = useSelection();
    const { childrenHNodes, id, spacingMode = 'default', disabled } = props || {};
    const [carouselWidth, setCarouselWidth] = useState(0);
    const [ready, setReady] = useState(false);
    const theme = useTheme();
    const { isConnected } = useNetwork();

    const scrollContainerRef = useRef(null);

    // Get spacing value based on mode
    const spacing = getSpacingValue(spacingMode, theme);

    // Calculate widths of tabs
    const widths = useCalculateWidths(carouselWidth, childrenHNodes, theme, isConnected);

    // Calculate total width for gradient visibility
    const totalTabsWidth = calculateTotalTabsWidth(widths.childrenWidths, spacing);

    // Manage gradient visibility based on scroll position
    const [gradientState, handleScroll] = useGradientVisibility(carouselWidth, totalTabsWidth);

    // Ref callback to measure scroll container and store reference
    const scrollContainerRefCallback = useCallback(node => {
        scrollContainerRef.current = node;
        if (node) {
            // Use ResizeObserver to wait for actual layout
            const observer = new ResizeObserver(entries => {
                for (const entry of entries) {
                    const width = entry.contentRect.width;
                    if (width > 0) {
                        setCarouselWidth(width);
                        setReady(true);
                    }
                }
            });
            observer.observe(node);

            // Cleanup
            return () => observer.disconnect();
        }
    }, []);

    // Handle scroll events to update gradient visibility
    const onScroll = useCallback(
        e => {
            const { scrollLeft, scrollWidth, clientWidth } = e.target;
            handleScroll(scrollLeft, scrollWidth, clientWidth);
        },
        [handleScroll]
    );

    // Handle mouse wheel for horizontal scrolling on desktop (Windows)
    const handleWheel = useCallback(e => {
        const container = scrollContainerRef.current;
        if (container) {
            // Only redirect if it's primarily vertical scrolling (standard mouse wheel)
            // If deltaX is significant, the user is likely using a trackpad or horizontal wheel
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.preventDefault();
                container.scrollLeft += e.deltaY;
            }
        }
    }, []);

    // Note: Auto-scrolling to selected tab removed - smooth scrolling handles visibility

    // Get gradient styles from theme
    const leftGradientStyle = getGradientStyles(theme, 'left');
    const rightGradientStyle = getGradientStyles(theme, 'right');

    // Hover-to-scroll functionality for gradient overlays
    const {
        isHoveringLeft,
        isHoveringRight,
        handleLeftMouseEnter,
        handleLeftMouseLeave,
        handleRightMouseEnter,
        handleRightMouseLeave
    } = useHoverScroll(scrollContainerRef);

    return rc(
        CarouselContainer,
        {},
        rc(
            Fragment,
            null,
            // Horizontal scroll container with tabs
            rc(
                ScrollContainer,
                {
                    ref: scrollContainerRefCallback,
                    onScroll,
                    onWheel: handleWheel,
                    'data-testid': `tabCarousel${id}`
                },
                rc(
                    TabsContainer,
                    {
                        spacing,
                        theme
                    },
                    ...childrenHNodes.map((childHNode, i) =>
                        rc(TabGL2025, {
                            key: i,
                            childHNode,
                            tabIndex: i,
                            selected: selectedIndex === i,
                            onClick: setSelectedIndex,
                            displayed: true,
                            calcedMaxWidth: ready ? (widths.childrenWidths[i] ?? 1000) : 1000,
                            totalTabs: childrenHNodes.length,
                            disabled
                        })
                    ),
                    rc(EmptyTabSpace, { theme })
                )
            ),

            // Left gradient overlay - hover to scroll left
            rc(LeftGradient, {
                visible: gradientState.showLeftGradient,
                gradientStyle: leftGradientStyle,
                theme,
                isHovering: isHoveringLeft,
                onMouseEnter: handleLeftMouseEnter,
                onMouseLeave: handleLeftMouseLeave
            }),

            // Right gradient overlay - hover to scroll right
            rc(RightGradient, {
                visible: gradientState.showRightGradient,
                gradientStyle: rightGradientStyle,
                theme,
                isHovering: isHoveringRight,
                onMouseEnter: handleRightMouseEnter,
                onMouseLeave: handleRightMouseLeave
            })
        )
    );
}

/**
 * Calculate width for each tab
 * Similar to original but simpler - all tabs are rendered
 */
function useCalculateWidths(containerWidth, children, theme, isConnected) {
    const [widths, setWidths] = useState({ containerWidth, childrenWidths: [], totalWidth: 0 });

    useEffect(() => {
        async function doAsync() {
            let totalWidth = 0;
            const childrenWidths = await Promise.all(
                children.map(async child => {
                    // Hidden tabs don't contribute to width
                    if (child.hideWhen && child.hideWhen.includes('ONLINE') === isConnected) return 0;

                    // Calculate padding from tab style (paddingLeft + paddingRight)
                    const tabPadding =
                        (theme.tabs.current.default.container.paddingLeft || 0) +
                        (theme.tabs.current.default.container.paddingRight || 0);
                    const maxTextWidth = containerWidth - tabPadding - (theme.tabs.gradient.width || 0);
                    const width = await textWidth.measure(child.title, theme.font, theme.fontSize || 14, maxTextWidth);
                    const childWidth = Math.ceil(width + tabPadding) + theme.tabs.spacing.default / 6; // Small margin
                    totalWidth += childWidth;
                    return childWidth;
                })
            );
            setWidths({ totalWidth, childrenWidths, containerWidth });
        }
        doAsync();
    }, [containerWidth, children, theme.font, theme.fontSize, theme.tabs, isConnected]);

    return widths;
}
